import type { ReactNode } from 'react';

export interface TabDef<TId extends string = string> {
  id: TId;
  label: ReactNode;
}

export interface TabsProps<TId extends string = string> {
  tabs: ReadonlyArray<TabDef<TId>>;
  active: TId;
  onChange: (id: TId) => void;
  className?: string;
}

// Two-layer glass background — tinted gradient on top of a dark base,
// driven by 5 independent CSS-var knobs (`bgFrom`/`bgTo`/`bgDirection`/
// `bgOpacity`/`darkTint`). Same shape as the My Packs cards so the
// design-constructor regulators look identical across surfaces. Kept
// literal (not behind a helper) so the invariant scanner can see every
// `--co-Tabs-*` token it depends on.
const TAB_GROUP_BG =
  'linear-gradient(' +
  'var(--co-Tabs-tabGroupBgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-Tabs-tabGroupBgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-Tabs-tabGroupBgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-Tabs-tabGroupBgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-Tabs-tabGroupBgOpacity, 1) * 100%), transparent) 100%), ' +
  'linear-gradient(90deg, ' +
  'var(--co-Tabs-tabGroupDarkTint, rgba(0,0,0,0.18)) 0%, ' +
  'var(--co-Tabs-tabGroupDarkTint, rgba(0,0,0,0.18)) 100%)';

const TAB_ACTIVE_BG =
  'linear-gradient(' +
  'var(--co-Tabs-tabActiveBgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-Tabs-tabActiveBgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-Tabs-tabActiveBgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-Tabs-tabActiveBgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-Tabs-tabActiveBgOpacity, 1) * 100%), transparent) 100%), ' +
  'linear-gradient(90deg, ' +
  'var(--co-Tabs-tabActiveDarkTint, rgba(0,0,0,0.18)) 0%, ' +
  'var(--co-Tabs-tabActiveDarkTint, rgba(0,0,0,0.18)) 100%)';

/**
 * Shared tab pill group. Every visual aspect (track / active pill
 * gradient, border, idle text colour & opacity, font size) is themed
 * via the `--co-Tabs-*` namespace, so adjusting Design Constructor's
 * "Tab pill group (shared)" card reflows every tablist using this
 * component. Inspect handle (`data-co-component="Tabs"`) is wired on
 * the tablist root.
 *
 * Generic over the tab id so callers can keep their own union types
 * (e.g. `'cards' | 'packs' | 'deliveries'`) without losing inference
 * on `active` / `onChange`.
 */
export function Tabs<TId extends string = string>({
  tabs,
  active,
  onChange,
  className = '',
}: TabsProps<TId>) {
  return (
    <div
      role="tablist"
      data-co-component="Tabs"
      className={
        'relative mx-auto flex max-w-[480px] items-center gap-[2px] ' +
        'rounded-full p-[2px] ' +
        className
      }
      style={{
        backgroundImage: TAB_GROUP_BG,
        border:
          '1px solid var(--co-Tabs-tabGroupBorder, rgba(171,174,179,0.24))',
        backdropFilter: 'blur(27px)',
        WebkitBackdropFilter: 'blur(27px)',
      }}
    >
      {tabs.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className="
              flex h-9 flex-1 items-center justify-center rounded-full
              px-3 transition-colors whitespace-nowrap
              text-[length:var(--co-Tabs-tabSize,13px)]
              @[600px]:h-[38px] @[600px]:text-[length:var(--co-Tabs-tabSize,15px)]
            "
            style={{
              backgroundImage: isActive ? TAB_ACTIVE_BG : 'none',
              border: 'none',
              color: isActive
                ? 'var(--co-Tabs-tabActiveText, #FFFFFF)'
                : 'var(--co-Tabs-tabIdleText, #FFFFFF)',
              opacity: isActive
                ? 1
                : 'var(--co-Tabs-tabIdleOpacity, 0.5)',
              fontWeight: 600,
              lineHeight: '22px',
              letterSpacing: '-0.3px',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
